import { Dashboard } from '@/common/dashboard/Dashboard';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { DimRearrangeButton } from '@/common/entityFramework/entityPerspective/support/DimRearrangeButton';
import { DropdownSelect } from '@/common/form/formFields/DropdownSelect';
import { useForm } from '@/common/form/FormContext';
import { EntitySelect } from '@/common/form/formFields/EntitySelect';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { ContentBox } from '@/common/layout/ContentBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { McdDispCodeOutcomeCards } from './McdDispCodeOutcomeCards';
import { McdDispOutcomeSummaryCards } from './McdDispOutcomeSummaryCards';
import { McdDispOutcomeTrendCard } from './McdDispOutcomeTrendCard';
import { hiddenPeriodFilter } from './useDispReqParam';

const title = 'Medicaid Dispute Outcome Dashboard';
const titleIcon = fas.faScaleBalanced;

const bucketOptions = [
  { value: 'CREATED', displayName: 'Created' },
  { value: 'ACCEPTED', displayName: 'Accepted' },
  { value: 'REJECTED_PAID', displayName: 'Rejected (Paid)' },
  { value: 'REJECTED_DUE', displayName: 'Rejected (Due)' },
  { value: 'OUTSTANDING', displayName: 'Outstanding Disputes' },
  { value: 'URA_REVISION', displayName: 'URA Revisions' },
];

// Both pickers offer 2015Q1 (the plugin's INV_PERIOD_FLOOR) up to the SC config's current Invoice Period
// (CURR_INV_PERIOD), which the filters plugin resolves
const qtrPeriodExpr = (currInvPeriodCode) =>
  ExprUtils.and(
    ExprUtils.is('periodicity', 'QTR'),
    ExprUtils.isGreaterOrEqual('code', '2015Q1'),
    ExprUtils.isLessOrEqual('code', currInvPeriodCode),
  );
const newestFirst = [{ colId: 'code', sort: 'desc' }];

// Form values an EntitySelect expects for a preselected set of Periods; no ids means All
const periodSelection = (field, ids) =>
  ids?.length ?
    {
      [field]: ExprUtils.whereParams('id', ids),
      [field + 'SelectedCount']: ids.length,
      [field + 'SelectedIds']: ids,
    }
  : { [field]: null };

// Same dimension set as McdInvFinDashboard - this dataset is a filtered subset of the same tables
const entityFilterFieldDefs = [
  {
    field: 'rbtPeriodExpr',
    displayName: 'Rebate Period',
    entity: 'PERIOD',
    code: 'RBT_PERIOD',
    module: 'sc',
    multiple: true,
    sortModel: newestFirst,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'rbtPeriodId',
  },
  {
    field: 'earnedPeriodExpr',
    displayName: 'Earned Period',
    entity: 'PERIOD',
    code: 'EARNED_PERIOD',
    module: 'sc',
    multiple: true,
    sortModel: newestFirst,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'earnedPeriodId',
  },
  {
    field: 'prodExpr',
    displayName: 'Product',
    entity: 'NDC11_PROD',
    code: 'NDC11_PROD',
    module: 'md',
    multiple: true,
  },
  {
    field: 'brandExpr',
    displayName: 'Brand',
    entity: 'BRAND_PROD',
    code: 'BRAND',
    module: 'md',
    multiple: true,
    hidden: true,
  },
  {
    field: 'stateExpr',
    displayName: 'State',
    entity: 'STATE',
    code: 'STATE',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'stateId',
  },
  {
    field: 'ctrtExpr',
    displayName: 'Contract',
    entity: 'CTRT',
    code: 'CTRT',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV',
    parentFilterFkField: 'ctrtId',
  },
  {
    field: 'dispCodes',
    displayName: 'Dispute Code',
    code: 'DISP_CODE',
    type: 'DROPDOWN',
    multiSelect: true,
  },
  {
    field: 'vendorExpr',
    displayName: 'Vendor',
    entity: 'CUST',
    code: 'VENDOR',
    module: 'md',
    multiple: true,
    hidden: true,
  },
];

const codeToFilter = Object.fromEntries(
  entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, { displayName: fieldDef.displayName }]),
);

const defaultVisibleFilters = entityFilterFieldDefs
  .filter((fieldDef) => !fieldDef.hidden)
  .map((fieldDef) => fieldDef.code);

const fieldDefByCode = Object.fromEntries(entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, fieldDef]));

const xAxisOptions = [
  {
    code: 'EARNED_PERIOD',
    displayName: 'Earned View',
    axisLabel: 'Earned Period',
    description: 'Group by the quarter the utilization occurred in.',
  },
  {
    code: 'RBT_PERIOD',
    displayName: 'Rebate View',
    axisLabel: 'Rebate Period',
    description: 'Group by the quarter the State invoiced in.',
  },
  { code: 'NDC11_PROD', displayName: 'Product View', axisLabel: 'Product', description: 'Group by NDC11.' },
  { code: 'BRAND', displayName: 'Brand View', axisLabel: 'Brand', description: 'Group by Brand.' },
  { code: 'STATE', displayName: 'State View', axisLabel: 'State', description: 'Group by State.' },
  { code: 'CTRT', displayName: 'Contract View', axisLabel: 'Contract', description: 'Group by Contract.' },
  { code: 'VENDOR', displayName: 'Vendor View', axisLabel: 'Vendor', description: 'Group by State vendor.' },
];

// Lives inside the Dashboard's Form so it can follow the trend card's x-axis
const DispFilters = ({ activeFieldDefs }) => {
  const { formValues } = useForm();
  const hiddenCode = hiddenPeriodFilter[formValues.xAxis]?.code;

  return (
    <FilterContainer>
      <DropdownSelect
        fieldDef={{
          field: 'bucket',
          displayName: 'Outcome',
          multiSelect: true,
          values: bucketOptions,
        }}
      />
      {activeFieldDefs
        .filter((fieldDef) => fieldDef.code !== hiddenCode)
        .map((fieldDef) =>
          fieldDef.type === 'DROPDOWN' ?
            <DropdownSelect key={fieldDef.field} fieldDef={fieldDef} />
          : <EntitySelect key={fieldDef.field} fieldDef={{ ...fieldDef, variant: 'outlined' }} />,
        )}
    </FilterContainer>
  );
};

export const McdDispDashboard = () => {
  const rsrc = useRsrc();

  const defaultFormValues = useRef();
  const [dimOptions, setDimOptions] = useState();
  const [visibleFilters, setVisibleFilters] = useNavStateSync('dispVisibleFilters', defaultVisibleFilters);

  useEffect(() => {
    Promise.all([
      // Same filter dimension set as McdInvFinDashboard - its filters plugin already provides everything this needs
      rsrc.data('sc', 'MCD_INV_DASHBOARD_FILTERS_DATA_PLUGIN', {}),
      rsrc.data('sc', 'ENUM_DATA_PLUGIN', { enumCode: 'MCD_DISP_TYPE' }),
    ]).then(([res, dispTypes]) => {
      // Uncoded matches lines with no Dispute Code, so selecting every option leaves nothing out
      const dispCodes = [
        ...dispTypes.map((dispType) => ({
          value: dispType.code,
          displayName: `${dispType.code}: ${dispType.displayName}`,
        })),
        { value: 'Uncoded', displayName: 'Uncoded (no Dispute Code)' },
      ];
      defaultFormValues.current = {
        bucket: bucketOptions.map((option) => option.value),
        measure: 'AMT',
        ctrtExpr: null,
        prodExpr: null,
        brandExpr: null,
        dispCodes: dispCodes.map((option) => option.value),
        vendorExpr: null,
        stateExpr: null,
        // Both periods start on the most recent quarters up to CURR_INV_PERIOD
        ...periodSelection('earnedPeriodExpr', res.recentPeriodIds),
        ...periodSelection('rbtPeriodExpr', res.recentPeriodIds),
        xAxis: 'EARNED_PERIOD',
      };
      setDimOptions({
        dispCodes,
        vendorExpr: ExprUtils.whereParams('id', res.vendorCustIds),
        periodExpr: qtrPeriodExpr(res.currInvPeriodCode),
      });
    });
  }, []);

  // Saved nav state may reference filters that no longer exist; options known only at runtime are attached here
  const activeFieldDefs = useMemo(() => {
    if (!dimOptions) return [];
    const runtimeProps = {
      DISP_CODE: { values: dimOptions.dispCodes },
      VENDOR: { expr: dimOptions.vendorExpr },
      RBT_PERIOD: { expr: dimOptions.periodExpr },
      EARNED_PERIOD: { expr: dimOptions.periodExpr },
    };
    return visibleFilters
      .map((code) => fieldDefByCode[code] && { ...fieldDefByCode[code], ...runtimeProps[code] })
      .filter(Boolean);
  }, [visibleFilters, dimOptions]);

  const resetDims = () => {
    setVisibleFilters(defaultVisibleFilters);
  };

  // The Dashboard's Form can only mount once its defaults are known, so until then show the card loading in place
  if (!dimOptions) {
    return (
      <ContentBox>
        <Title icon={titleIcon} title={title} />
        <SpacerLayout>
          <Spacer weights={[2, 1]}>
            <DashboardCard />
          </Spacer>
        </SpacerLayout>
      </ContentBox>
    );
  }

  const augButtons = [
    <DimRearrangeButton
      key="filterRearrangeButton"
      setDims={setVisibleFilters}
      codeToDim={codeToFilter}
      activeDims={activeFieldDefs.map(({ code }) => ({ code }))}
    />,
  ];

  const dispCodeCount = dimOptions.dispCodes.length;

  return (
    <Dashboard
      title={title}
      titleIcon={titleIcon}
      defaultFormValues={defaultFormValues.current}
      augButtons={augButtons}
      onReset={resetDims}
    >
      <DispFilters activeFieldDefs={activeFieldDefs} />
      <SpacerLayout>
        <Spacer weights={[2, 1]}>
          <Spacer vertical weights={[1, 4]}>
            <McdDispOutcomeSummaryCards dispCodeCount={dispCodeCount} />
            <Spacer weights={[1]}>
              <McdDispOutcomeTrendCard xAxisOptions={xAxisOptions} dispCodeCount={dispCodeCount} />
            </Spacer>
          </Spacer>
          <McdDispCodeOutcomeCards dispCodeCount={dispCodeCount} />
        </Spacer>
      </SpacerLayout>
    </Dashboard>
  );
};
